import React, { FC, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, generatePath, useParams, useLocation } from 'react-router-dom';
import saga from '../saga';
import reducer from '../reducer';
import routes from '../../../config/routes';
import history from '../../../utils/history';
import queryString from 'query-string';
import {encodeData} from '../../../utils/url';

// Import Styles
import styled from 'styled-components';
import Container from '../../../components/Layout/Container';
import H3 from '../../../components/Headings/H3';
import Row from '../../../components/Layout/Row';
import Col4 from '../../../components/Layout/Grid/Col4';
import Col8 from '../../../components/Layout/Grid/Col8';
import {LoadOverlaySticky} from '../../../components/Animations';
import Filter from '../../../components/DashboardHeadingFilterSection';
import NoResults from '../../../components/NoResults';

// Import Model
import { Job } from '../../../models/jobs';

// Import Paginate & Scroll
import ReactPaginate from 'react-paginate';
import PaginateWrapper from '../../../components/Pagination';
import ScrollToTop, { paginateScroll } from '../../ScrollToTop';

// Import Messages For
import { FormattedMessage } from 'react-intl';
import intl from '../../../intl';
import generalMessages from '../../generalMessages';
import messages from '../messages';

// Import Actions
import { getEmployerJobs, getEmployer } from '../actions';

import { selectCategory } from '../../App/actions';
import { makeSelectCategories } from '../../App/selectors';
import { GROUP_CATEGORIES, REDUX_SAGA_KEYS } from '../../../config/config';
import CustomHelmet from '../../../components/Helmet'

import {optionsFilterJobs} from '../../../config/selectOptions'
// Import Selectors
import { loadingSelector, employerJobsSelector, employerSelector, makeSelectCurrentPage, makeSelectPageCount, loadingEmployerJobsSelector } from '../selectors';
import { ParamTypes } from '../../../models/urlParams';
import ActiveJobCard from '../../../components/PageSections/Job/ActiveJobCard';
import Separator from '../../../components/PageSections/Separator';
import Title from '../../../components/PageSections/Employer/Title';
import MainWrapper from '../../../components/PageSections/MainWrapper';
import Description from '../../../components/PageSections/Employer/Description';
import colors from '../../../config/colors';
import { useInjectSagaAndReducer } from '../../../utils/helpers';

const EmployerDetails: FC = () => {
  
  useInjectSagaAndReducer(REDUX_SAGA_KEYS.employers, reducer, saga);

  let {company_slug} = useParams<ParamTypes>();

  let {search} = useLocation();

  const location = useLocation();

  const dispatch = useDispatch();



  const employer = useSelector(employerSelector);
  const employerJobs = useSelector(employerJobsSelector);
  const loadingEmployerJobs = useSelector(loadingEmployerJobsSelector);
  const selectCategories = useSelector(makeSelectCategories);
  const loading = useSelector(loadingSelector);
  const pageCount = useSelector(makeSelectPageCount);
  const currentPage = useSelector(makeSelectCurrentPage);

  // Default State Filter
  const [filter, setFilter] = useState('');
  const [parsed, setParsed] = useState<any>(null);

  useEffect(() => {
    if(company_slug){
      dispatch(getEmployer(company_slug));
    }
  }, [dispatch, company_slug]);

  useEffect(() => {
    let page = 1;
    let parsed = queryString.parse(search);
    if(parsed && parsed.kategorija){
      parsed['field_of_work'] = parsed.kategorija;
    }
    setParsed(parsed);
    if(employer) {
      dispatch(getEmployerJobs(page, employer.unique_id, filter, encodeData(parsed)));
    }
  }, [dispatch, search, employer, filter]);

  const handlePageClick = useCallback((data: any) => {
    let page = data.selected >= 0 ? data.selected + 1 : 0;
    let parsed = queryString.parse(search);
    // Paginate Scroll Helper
    paginateScroll();
    if(employer){
      dispatch(getEmployerJobs(page, employer.unique_id, filter, encodeData(parsed)));
    }
  }, [dispatch, search, employer, filter]);

   // Categories
   useEffect(() => {
    if(selectCategories.length === 0){
      dispatch(selectCategory(GROUP_CATEGORIES.area_of_work));
    }
  }, [dispatch, selectCategories]);

  return (
    <EmployerDetailsWrapper>
      <CustomHelmet
        title={`Odaberi svoj novi posao koji je na samo klik od tebe! Pretraži aktuelne oglase za posao!`}
        pageUrl={location ? location.pathname : ''}
        description={`Tražiš POSAO? Pretraži različite oglase za posao po kompaniji, industriji, lokaciji i ostalim filterima. Pronađi svoj budući posao već danas!`}
        ogTitle={`Odaberi svoj novi posao koji je na samo klik od tebe! Pretraži aktuelne oglase za posao!`}
        ogDescription={`Tražiš POSAO? Pretraži različite oglase za posao po kompaniji, industriji, lokaciji i ostalim filterima. Pronađi svoj budući posao već danas!`}
      />
      <ScrollToTop />
       {
         loading ? <LoadOverlaySticky /> : <></> ?
        <>
 
      <Separator/>
      <Container>
        <MainWrapper>
          <Title employer={employer} />
          <Description employer={employer} />
          <Row>
            <Col4Job>
              <H3><FormattedMessage {...messages.JobsCategory} /></H3>
              <SearchByCategory>
                {employer ?
                  <li><Link to={employer.company_slug ? generatePath(routes.EmployerDetails, {'company_slug' : employer.company_slug}) : '#'}><FormattedMessage {...messages.AllCategories} /></Link></li>
                  : null}

                {selectCategories && selectCategories.length > 0 ?
                  selectCategories.map((item: any, i: number) => {
                    return (
                      <li key={i}  ><span className={parsed && item.value === parsed.field_of_work ? 'active' : ''} onClick={ ()=> {
                        if(employer) {
                          history.push(`${generatePath(routes.EmployerDetails, {'company_slug': employer.company_slug})}?kategorija=${item.value}`)
                        }

                        }}
                      >{item.label}</span></li>
                    )
                  })
                : null}
              </SearchByCategory>
            </Col4Job>
            <Col8Job>
              <Filter
                filter={true}
                onChangeFilter={setFilter}
                optionsFilter={optionsFilterJobs}
                headerText={intl.formatMessage(messages.ActiveJobs)}
                selectValue={filter}
              />
              {loadingEmployerJobs ?
                <LoadOverlaySticky />
                : <>
                {employerJobs && employerJobs.length > 0 ?
                  employerJobs.map((item: Job, i: string) => {
                    return (
                      <ActiveJobCard item={item} key={i} />
                    )
                  })
                :
                <NoResults message={intl.formatMessage(generalMessages.NoJobsEmptyMessage)} />
                }
                {pageCount > 1 &&
                  <PaginateWrapper>
                    <ReactPaginate
                      previousLabel={<svg><use xlinkHref={`#arrow-left`} /></svg>}
                      nextLabel={<svg><use xlinkHref={`#arrow-right`} /></svg>}
                      pageCount={pageCount}
                      forcePage={currentPage - 1}
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={2}
                      onPageChange={handlePageClick}
                      containerClassName={'pagination-white'}
                      disabledClassName={"disabled"}
                      activeClassName={'active'}
                    />
                  </PaginateWrapper>
                }
                </>
              }      
            </Col8Job>
          </Row>
        </MainWrapper>
      </Container>
      </> : null
      }
    </EmployerDetailsWrapper>
  )
}

const EmployerDetailsWrapper = styled.div``;

const SearchByCategory = styled.div`
  background: ${colors.GRAY_EXTRA_LIGHT};
  padding: 10px 20px;
  overflow-y: auto;
  max-height: 380px;
  li {
    list-style: none;
    a, span {
      color: ${colors.BLACK};
      background: ${colors.WHITE};
      box-shadow: 0px 0px 40px rgba(198,198,198,0.16);
      border-radius: 10px;
      display: flex;
      align-items: center;
      padding: 15px;
      margin: 10px 0;
      cursor: pointer;
      font-family: "Gilroy-Medium";
      &:hover {
        background: ${colors.PRIMARY_ORANGE};
        color: ${colors.WHITE};
      }
      &.active {
        background: ${colors.PRIMARY_ORANGE};
        color: ${colors.WHITE};
      }
    }
  }
  @media (max-width: 769px) {
    max-height: 245px;
  }
`;

const Col4Job = styled(Col4)`
   @media (max-width: 1025px) {
      flex: 0 0 100%;
      max-width: 100%;
    }
`;

const Col8Job = styled(Col8)`
  padding-top: 20px;
  background: ${colors.GRAY_EXTRA_LIGHT};
  select {
    background: ${colors.WHITE};
  }
   @media (max-width: 1025px) {
      flex: 0 0 100%;
      max-width: 100%;
    }
`;

export default EmployerDetails;
